import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { showError } from '@/lib/utils/notifications'
import { downloadNewEorpPdf, fetchEorpNew } from '@/lib/api/eorpService'
import styles from './NewAgreementModal.module.css'

export interface NewAgreementModalProps {
  isOpen: boolean
  onClose: () => void
}

const EORP_MODAL_TITLE = 'Explanation of the Reimbursement Process'

/**
 * Reusable EORP popup. Self-loads the latest EORP HTML on open.
 * Errors are intentionally silent (matches Angular new-agreement-popup behavior).
 */
export function NewAgreementModal({ isOpen, onClose }: NewAgreementModalProps) {
  const [section, setSection] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setLoading(true)
    setErrored(false)
    setPdfLoading(false)
    fetchEorpNew()
      .then((res) => {
        if (cancelled) return
        setSection(res.eorp_section ?? '')
      })
      .catch(() => {
        if (!cancelled) setErrored(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  const handleDownloadPdf = async () => {
    if (!section || loading || errored || pdfLoading) return

    setPdfLoading(true)
    try {
      const blob = await downloadNewEorpPdf()
      if (!blob || blob.size === 0) {
        showError('Empty PDF received.')
        return
      }
      if (blob.type && blob.type.includes('json')) {
        showError('Unable to get the PDF. Please try again.')
        return
      }

      const objectUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = 'Explanation-of-Reimbursement-Process.pdf'
      anchor.style.display = 'none'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000)
    } catch {
      showError('Unable to get the PDF. Please try again.')
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title={EORP_MODAL_TITLE}
      onClose={onClose}
      maxWidth="max-w-800"
      headerVariant="centeredTitle"
      bodyScrollOnly
      panelClassName={styles.eorpModalPanel}
      headerClassName={styles.eorpModalHeader}
      bodyClassName={styles.eorpModalBody}
      footerClassName={styles.eorpModalFooter}
      closeButtonDataTestId="new-agreement-modal-close-button"
      titleDataTestId="new-agreement-modal-title"
      panelDataTestId="new-agreement-modal-panel"
      footer={
        <div className="flex flex-wrap items-center justify-center gap-3">
          {!loading && !errored && section ? (
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              data-testid="new-agreement-modal-download-pdf-button"
              className={styles.eorpSecondaryButton}
            >
              {pdfLoading ? 'Preparing…' : 'Download PDF'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            data-testid="new-agreement-modal-ok-button"
            className={styles.eorpPrimaryButton}
          >
            OK
          </button>
        </div>
      }
    >
      {loading && (
        <div
          className="flex justify-center py-8"
          data-testid="new-agreement-modal-loading"
        >
          <Loader2
            className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
            aria-label="Loading"
          />
        </div>
      )}
      {errored && !loading && (
        <p className="text-sm text-slate-500">Document Loading Error</p>
      )}
      {!loading && !errored && section && (
        <div
          className={styles.eorpSectionHtml}
          dangerouslySetInnerHTML={{ __html: section }}
          data-testid="new-agreement-content"
        />
      )}
      {!loading && !errored && !section && (
        <p className="text-sm text-slate-500">No details available.</p>
      )}
    </OnScreenModal>
  )
}
